import React from 'react';
import { PlayCircle, Star } from 'lucide-react';
import SiteImage from '../assets/header/hero.png';
import ChromeIcon from '../assets/header/gc.png';

const HeroSection = () => {
  const Particle = ({ style }) => (
    <div
      className="particle w-4 h-4 absolute rounded-full bg-gradient-to-r from-purple-400 to-cyan-400 opacity-10"
      style={style}
    />
  );

  return (
    <section className="relative bg-gray-50 dark:bg-gray-900 md:py-20 sm:py-10 overflow-hidden">
      {/* Google-themed ambient background effects */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl animate-pulse bg-[#4285F4]"></div>
        <div className="absolute top-1/3 right-1/4 w-80 h-80 rounded-full blur-2xl animate-pulse bg-[#EA4335]" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-1/4 left-1/3 w-72 h-72 rounded-full blur-3xl animate-pulse bg-[#FBBC04]" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-1/3 right-1/3 w-64 h-64 rounded-full blur-2xl animate-pulse bg-[#34A853]" style={{ animationDelay: '3s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          
          <div data-aos="fade-right" className='relative z-10'>
            <div className='relative p-10'>
                {/* Material Design Chrome Badge */}
                <div className="absolute top-0 right-0 flex items-center space-x-2 glass-card px-4 py-2 rounded-2xl shadow-xl animate-fadeIn">
                  <img src={ChromeIcon} alt="Chrome Logo" className="w-5 h-5" />
                  <span className="text-sm font-bold text-gray-900 dark:text-white">Chrome Extension</span>
                </div>

                {/* Google-colored title */}
                <h1 className="text-4xl md:text-6xl font-bold mb-6 relative animate-fadeInUp text-gray-900 dark:text-white">
                  <span className="text-[#4285F4]">Auto</span>
                  <span className="text-[#EA4335]">mate</span>
                  <span className="text-gray-900 dark:text-white"> Your </span>
                  <span className="text-[#FBBC04]">AI</span>
                  <span className="text-gray-900 dark:text-white"> Content </span>
                  <span className="text-[#34A853]">Creation</span>
                </h1>

                {/* Google colored sparkles */}
                <div className="absolute -top-4 left-10 w-3 h-3 bg-[#4285F4] rounded-full animate-bounce opacity-70"></div>
                <div className="absolute -top-2 right-20 w-2 h-2 bg-[#EA4335] rounded-full animate-bounce opacity-70" style={{ animationDelay: '0.2s' }}></div>
                <div className="absolute top-16 left-32 w-2 h-2 bg-[#FBBC04] rounded-full animate-bounce opacity-70" style={{ animationDelay: '0.4s' }}></div>

                <p className="text-xl mb-8 text-gray-700 dark:text-gray-300 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
                  Generate high-quality content at scale with one-click workflows
                  for Google Labs AI tools.
                </p>

                <div className="flex flex-wrap gap-4 animate-fadeInUp" style={{ animationDelay: '0.4s' }}>
                  <a
                    href="https://chromewebstore.google.com/detail/glabs-automator-automate/lgijlcogpfkinhgaijjbocflajljjpen?utm_source=item-share-cb"
                    className="relative group overflow-hidden bg-gradient-to-r from-[#34A853] to-[#0f9d58] dark:from-[#8B5CF6] dark:to-[#7C3AED] text-white font-bold px-8 py-4 rounded-2xl transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105 ripple-effect"
                  >
                    <span className="relative z-10">Install it. It's Free!</span>
                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </a>
                  <a
                    href="https://youtu.be/Do85QlO1UOU?si=i9jYVcBS2PCtgkLM" target='_blank' rel='noreferrer'
                    className="glass-card font-bold px-8 py-4 rounded-2xl hover:bg-white/90 dark:hover:bg-gray-800/90 transition-all duration-300 flex items-center shadow-xl hover:shadow-2xl transform hover:scale-105 ripple-effect text-gray-900 dark:text-white group"
                  >
                    <PlayCircle className="mr-2 h-5 w-5 transition-transform duration-300 group-hover:scale-110" />
                    Watch Demo
                  </a>
                </div>

                <div style={{ marginTop: '2rem' }} className="animate-fade-in-up delay-800">
                  <a href="https://www.producthunt.com/products/g-labs-automator" target="_blank" className="transition-transform hover-scale" style={{ display: 'inline-block' }}>
                    <img src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=955610&theme=light&t=1747588463557" alt="AutoFly - Firefly bulk image generator - chrome web store | Product Hunt" style={{ width: '250px', maxWidth: '100%', height: 'auto' }} width="250" height="54" />
                  </a>
                </div>

                <div className="mt-8 flex flex-wrap gap-4 items-center animate-fadeInUp" style={{ animationDelay: '0.6s' }}>
                  <div className="flex -space-x-2">
                    <img
                      src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop&crop=face"
                      className="w-10 h-10 rounded-full border-2 border-white dark:border-gray-800 shadow-lg"
                      alt="User"
                    />
                    <img
                      src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=face"
                      className="w-10 h-10 rounded-full border-2 border-white dark:border-gray-800 shadow-lg"
                      alt="User"
                    />
                    <img
                      src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop&crop=face"
                      className="w-10 h-10 rounded-full border-2 border-white dark:border-gray-800 shadow-lg"
                      alt="User"
                    />
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Trusted by 10,000+ creators</p>
                    <div className="flex items-center">
                      {[1, 2, 3, 4, 5].map((star, index) => (
                        <Star
                          key={`star-${index}`}
                          className="text-[#FBBC04] w-4 h-4 fill-current"
                        />
                      ))}
                      <span className="ml-2 text-sm font-semibold text-gray-900 dark:text-white">4.9/5</span>
                    </div>
                  </div>
                </div>
              </div>

          </div>

          <div data-aos="fade-left" className="relative group">
            <div className="relative overflow-hidden rounded-3xl glass-card p-2 shadow-2xl">
              <img
                src={SiteImage}
                alt="GLabs Automator Interface"
                className="transition-all duration-500 group-hover:scale-105 rounded-2xl"
              />
            </div>

            {/* Google-colored decorative elements */}
            <div className="absolute -bottom-4 -right-4 bg-[#FBBC04] p-4 rounded-2xl shadow-2xl animate-bounce">
              <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>

            <div className="absolute -top-4 -left-4 bg-[#EA4335] p-3 rounded-full shadow-2xl animate-pulse">
              <Star className="text-white h-6 w-6 fill-current" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
